import { clsx } from 'clsx';
import { type KeyboardEvent, type ReactNode, useRef } from 'react';

export type TabItem<K extends string = string> = {
  key: K;
  label: ReactNode;
  /** Small count shown after the label. */
  count?: number;
  disabled?: boolean;
};

export type TabsProps<K extends string> = {
  tabs: readonly TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Prefix for tab/panel ids: tab `${idPrefix}-tab-${key}` controls panel `${idPrefix}-panel-${key}`. */
  idPrefix?: string;
  className?: string;
  'aria-label'?: string;
};

/**
 * Underlined tab list (roving tabindex; ←/→/Home/End move and activate). Render the active panel yourself,
 * optionally with `<TabPanel>` so aria-controls resolves.
 */
export function Tabs<K extends string>({ tabs, value, onChange, idPrefix = 'tabs', className, 'aria-label': ariaLabel }: TabsProps<K>) {
  const listRef = useRef<HTMLDivElement>(null);
  const enabled = tabs.filter((t) => !t.disabled);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = enabled.findIndex((t) => t.key === value);
    let next: TabItem<K> | undefined;
    if (event.key === 'ArrowRight') next = enabled[(index + 1) % enabled.length];
    else if (event.key === 'ArrowLeft') next = enabled[(index - 1 + enabled.length) % enabled.length];
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;
    event.preventDefault();
    onChange(next.key);
    listRef.current?.querySelector<HTMLElement>(`[data-tab-key="${next.key}"]`)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={clsx('flex items-center gap-4 overflow-x-auto border-b border-slate-200', className)}
    >
      {tabs.map((tab) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.key}`}
            aria-controls={`${idPrefix}-panel-${tab.key}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={tab.disabled}
            data-tab-key={tab.key}
            onClick={() => onChange(tab.key)}
            className={clsx(
              '-mb-px inline-flex h-9 shrink-0 items-center gap-1.5 border-b-2 px-0.5 text-[13px] font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ember-500 disabled:opacity-40',
              selected ? 'border-ember-500 text-slate-900' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={clsx(
                  'rounded-full px-1.5 text-2xs tabular',
                  selected ? 'bg-ember-100 text-ember-700' : 'bg-slate-100 text-slate-500',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export type TabPanelProps = { tabKey: string; idPrefix?: string; className?: string; children: ReactNode };

export function TabPanel({ tabKey, idPrefix = 'tabs', className, children }: TabPanelProps) {
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${tabKey}`} aria-labelledby={`${idPrefix}-tab-${tabKey}`} className={className}>
      {children}
    </div>
  );
}
